import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { Badge } from '@/components/ui/badge'
import { CircleSlash2Icon, LockKeyhole } from 'lucide-react'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import EventRaidReportsBadge from '../TeamEvent/components/EventRaidReportsBadge'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import { Link } from 'react-router-dom'

dayjs.extend(RelativeTime)

const shouldShowRaidReportBadge = (event: wowaudit.EventBasicReference) =>
  dayjs(event.startsAt).isBefore(dayjs())

export const eventTitleColumn = <TEvent extends wowaudit.EventBasicReference>(
  team?: wowaudit.Team,
): ColumnDef<TEvent> => ({
  id: 'title',
  header: 'Event',
  cell: ({ row }) => {
    const event = row.original
    const status = typeof event.status === 'string' ? event.status : undefined
    const activityType =
      typeof event.activityType === 'string' ? event.activityType : undefined
    const primaryDifficulty =
      typeof event.primaryDifficulty === 'string'
        ? event.primaryDifficulty
        : undefined
    const optional = event.optional === true
    return (
      <Link
        to={team ? `${team.path}/events/${event.id}` : ''}
        className='flex items-center gap-2'
      >
        <EventAvatar
          eventReference={event as unknown as wowaudit.EventReference}
          className='h-8 w-8 rounded-full'
        />
        <div className='min-w-0 leading-tight'>
          <div className='truncate'>{event.title || 'Event'}</div>
          <div className='flex items-center gap-1 text-xs text-muted-foreground'>
            {status === 'locked' ? (
              <LockKeyhole className='h-3 w-3 text-warning-foreground' />
            ) : null}
            {status === 'cancelled' ? (
              <CircleSlash2Icon className='h-3 w-3 text-destructive-foreground' />
            ) : null}
            {activityType ? (
              <span className='capitalize'>{activityType}</span>
            ) : null}
            {primaryDifficulty && activityType === 'raids' ? (
              <span>
                -{' '}
                {
                  difficultiesLabels[
                    primaryDifficulty as keyof typeof difficultiesLabels
                  ]
                }
              </span>
            ) : null}
            {optional ? (
              <span className='text-xs text-info-foreground'>(optional)</span>
            ) : null}
          </div>
        </div>
      </Link>
    )
  },
})

export const eventDateColumn = <
  TEvent extends wowaudit.EventBasicReference,
>(): ColumnDef<TEvent> => ({
  id: 'date',
  header: 'Date',
  cell: ({ row }) => {
    const event = row.original
    return (
      <div className='leading-tight whitespace-nowrap'>
        <div>{dayjs(event.startsAt).fromNow()}</div>
        <div className='text-xs text-muted-foreground'>
          {event.date ? dayjs(event.date).format('ddd, D MMM YYYY') : '-'}
        </div>
      </div>
    )
  },
})

export const eventTimeColumn = <
  TEvent extends wowaudit.EventBasicReference,
>(): ColumnDef<TEvent> => ({
  id: 'time',
  header: 'Time',
  cell: ({ row }) => {
    const event = row.original
    return (
      <div className='whitespace-nowrap'>
        {event.startTime || '--:--'} - {event.finishTime || '--:--'}
      </div>
    )
  },
})

type EventRaidReportsColumnOptions = {
  raidReportsByEventId: Record<string, number[]>
  isLoading: boolean
  onOpen: (eventId: number) => void
  headerLabel?: string
}

export const eventRaidReportsColumn = <
  TEvent extends wowaudit.EventBasicReference,
>({
  raidReportsByEventId,
  isLoading,
  onOpen,
  headerLabel = 'Report',
}: EventRaidReportsColumnOptions): ColumnDef<TEvent> => ({
  id: 'raid_reports',
  header: headerLabel,
  cell: ({ row }) => {
    const event = row.original
    if (!shouldShowRaidReportBadge(event)) return null

    return (
      <div
        onClick={(clickEvent) => {
          clickEvent.stopPropagation()
        }}
      >
        <EventRaidReportsBadge
          isLoading={isLoading}
          hasLinkedReport={
            (raidReportsByEventId[event.id.toString()]?.length || 0) > 0
          }
          onClick={() => onOpen(event.id)}
        />
      </div>
    )
  },
})

export const eventRaidReportsSummaryBadge = ({
  linkedCount,
  totalCount,
  onClick,
}: {
  linkedCount: number
  totalCount: number
  onClick: () => void
}) => (
  <button type='button' onClick={onClick}>
    <Badge variant='outline' className='text-xs h-5'>
      {linkedCount} / {totalCount}
    </Badge>
  </button>
)
